import { create } from "zustand";

import api from "../utils/api";
import { getError } from "../utils/getError";
import {
  buildFleetSnapshot,
  focusUnitOnMap,
} from "../Layouts/monitoreo-ia/services/fleetSnapshot";

const TURN_TIMEOUT_MS = 120000;
const SLOW_REQUEST_WARNING_MS = 60000;

export interface ChatToolCall {
  name: string;
  input?: Record<string, unknown>;
  is_error?: boolean;
}

export interface ChatUiAction {
  name: string;
  input?: Record<string, unknown>;
}

/** One change the assistant proposed and will not apply until a person says yes. */
export interface ChatPendingChange {
  name: string;
  module: string | null;
  action: string;
  description: string;
  method: string | null;
  path: string | null;
  input: Record<string, unknown>;
}

export interface ChatPendingAction {
  id: string;
  actions: ChatPendingChange[];
}

export const CHAT_CONTEXT_IDS = [
  "monitoreo",
  "mapa",
  "reportes",
  "envios",
  "unidades",
  "admin",
  "plataforma",
  "general",
] as const;

export type ChatContextId = (typeof CHAT_CONTEXT_IDS)[number];

export interface SendMessageOptions {
  /** El tema de la pregunta. En el chat a pantalla completa lo elige la persona. */
  context?: ChatContextId;
  /** La pantalla real. Decide si puede ofrecerse una herramienta que mueve la interfaz. */
  surface?: ChatContextId;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  isLoading?: boolean;
  toolCalls?: ChatToolCall[];
}

export interface ChatThreadSummary {
  id: string;
  title: string;
  updated_at: number | null;
}

interface StoredThread {
  id: string;
  title: string;
  updated_at?: { _seconds?: number } | number | null;
}

interface StoredMessage {
  id: string;
  role: string;
  content: string;
  tool_calls?: ChatToolCall[];
  created_at?: { _seconds?: number } | number | null;
}

const THREAD_STORAGE_KEY = "aiChatThreadId";

const secondsOf = (value: StoredThread["updated_at"]): number | null => {
  if (typeof value === "number") return value;
  return typeof value?._seconds === "number" ? value._seconds * 1000 : null;
};

const readStoredThreadId = (): string | null => {
  try {
    return localStorage.getItem(THREAD_STORAGE_KEY);
  } catch {
    return null;
  }
};

const rememberThreadId = (id: string | null) => {
  try {
    id ? localStorage.setItem(THREAD_STORAGE_KEY, id) : localStorage.removeItem(THREAD_STORAGE_KEY);
  } catch {
    return;
  }
};

interface ChatTurnPayload {
  thread_id?: string;
  reply?: string;
  tool_calls?: ChatToolCall[];
  ui_actions?: ChatUiAction[];
  pending_action?: ChatPendingAction | null;
}

const UI_ACTIONS: Record<string, (input: Record<string, unknown>) => void> = {
  ui_focus_map: (input) => {
    typeof input.unit === "string" && focusUnitOnMap(input.unit);
  },
};

const applyUiActions = (actions: ChatUiAction[] = []) => {
  actions.forEach((action) => UI_ACTIONS[action.name]?.(action.input ?? {}));
};

interface AiChatState {
  messages: ChatMessage[];
  isLoading: boolean;
  isSlowRequest: boolean;
  threadId: string | null;
  /** The change awaiting a decision, or null. Only ever one at a time. */
  pendingAction: ChatPendingAction | null;
  isDeciding: boolean;
  threads: ChatThreadSummary[];
  isLoadingThreads: boolean;
  isOpeningThread: boolean;
  threadsError: string | null;
  decideAction: (decision: "approve" | "reject") => Promise<void>;
  addMessage: (message: Omit<ChatMessage, "id" | "timestamp">) => string;
  updateMessage: (id: string, content: string, toolCalls?: ChatToolCall[]) => void;
  removeMessage: (id: string) => void;
  setLoading: (loading: boolean) => void;
  clearMessages: () => void;
  sendMessage: (content: string, options?: SendMessageOptions) => Promise<void>;
  cancelSlowRequest: () => void;
  loadThreads: () => Promise<void>;
  openThread: (id: string) => Promise<boolean>;
  removeThread: (id: string) => Promise<void>;
  restoreLastThread: () => Promise<boolean>;
}

function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export const useAiChatStore = create<AiChatState>((set, get) => {
  let abortController: AbortController | null = null;
  let slowRequestTimeoutId: NodeJS.Timeout | null = null;

  return {
  messages: [],
  isLoading: false,
  isSlowRequest: false,
  threadId: null,
  pendingAction: null,
  isDeciding: false,
  threads: [],
  isLoadingThreads: false,
  isOpeningThread: false,
  threadsError: null,

  addMessage: (message) => {
    const id = generateId();
    const newMessage: ChatMessage = {
      ...message,
      id,
      timestamp: Date.now(),
    };
    set((state) => ({ messages: [...state.messages, newMessage] }));
    return id;
  },

  updateMessage: (id, content, toolCalls) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id
          ? { ...msg, content, isLoading: false, ...(toolCalls ? { toolCalls } : {}) }
          : msg,
      ),
    }));
  },

  removeMessage: (id) => {
    set((state) => ({
      messages: state.messages.filter((msg) => msg.id !== id),
    }));
  },

  setLoading: (loading) => set({ isLoading: loading }),

  clearMessages: () => {
    rememberThreadId(null);
    set({ messages: [], threadId: null, pendingAction: null });
  },

  decideAction: async (decision) => {
    const { pendingAction, addMessage, updateMessage, removeMessage } = get();
    if (!pendingAction) return;

    // Cleared before the request, not after: the card must not stay on screen
    // offering a second click while the first is still in flight. The backend
    // guards against that too, in a transaction, but a button that looks live
    // and does nothing is its own bug.
    set({ pendingAction: null, isDeciding: true });

    const assistantId = addMessage({ role: "assistant", content: "", isLoading: true });

    try {
      const res = await api.post(
        "/ai/confirm",
        { action_id: pendingAction.id, decision },
        { timeout: TURN_TIMEOUT_MS },
      );

      const payload: ChatTurnPayload = res.data?.data?.data ?? {};

      updateMessage(assistantId, payload.reply?.trim() ?? "", payload.tool_calls);
      applyUiActions(payload.ui_actions);
      // A confirmation can itself end on another proposal (a multi-step change).
      if (payload.pending_action) set({ pendingAction: payload.pending_action });
    } catch (error) {
      removeMessage(assistantId);
      addMessage({ role: "assistant", content: getError(error) });
    } finally {
      set({ isDeciding: false });
    }
  },

  sendMessage: async (content: string, options: SendMessageOptions = {}) => {
    const { threadId, addMessage, updateMessage, removeMessage } = get();

    const text = content.trim();
    if (text.length === 0) return;

    console.log("[aiChatStore] sendMessage:", {
      messagePreview: text.substring(0, 50),
      options,
      context: options.context,
      surface: options.surface,
    });

    addMessage({ role: "user", content: text });
    set({ isLoading: true, isSlowRequest: false });

    const assistantId = addMessage({
      role: "assistant",
      content: "",
      isLoading: true,
    });

    // Taken at send time, not subscribed to: the socket rewrites positions
    // several times a second and the chat lives in the header, so watching it
    // would re-render every page on every report.
    const state = buildFleetSnapshot();

    abortController = new AbortController();
    slowRequestTimeoutId = setTimeout(() => {
      set({ isSlowRequest: true });
    }, SLOW_REQUEST_WARNING_MS);

    try {
      const payload = {
        message: text,
        ...(threadId ? { thread_id: threadId } : {}),
        ...(options.context ? { context: options.context } : {}),
        ...(options.surface ? { surface: options.surface } : {}),
        ...(state ? { state } : {}),
      };

      console.log("[aiChatStore] API request payload:", {
        message: payload.message.substring(0, 50),
        context: payload.context,
        surface: payload.surface,
      });

      const res = await api.post(
        "/ai/chat",
        payload,
        { timeout: TURN_TIMEOUT_MS, signal: abortController.signal },
      );

      const response: ChatTurnPayload = res.data?.data?.data ?? {};

      if (response.thread_id) {
        rememberThreadId(response.thread_id);
        set({ threadId: response.thread_id });
      }

      updateMessage(assistantId, response.reply?.trim() ?? "", response.tool_calls);
      applyUiActions(response.ui_actions);
      set({ pendingAction: response.pending_action ?? null });
    } catch (error) {
      if (!(error instanceof Error && error.name === "AbortError")) {
        removeMessage(assistantId);
        addMessage({ role: "assistant", content: getError(error) });
      }
    } finally {
      if (slowRequestTimeoutId) clearTimeout(slowRequestTimeoutId);
      slowRequestTimeoutId = null;
      abortController = null;
      set({ isLoading: false, isSlowRequest: false });
    }
  },

  cancelSlowRequest: () => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    if (slowRequestTimeoutId) {
      clearTimeout(slowRequestTimeoutId);
      slowRequestTimeoutId = null;
    }
    set({ isSlowRequest: false });
  },

  loadThreads: async () => {
    set({ isLoadingThreads: true, threadsError: null });
    try {
      const res = await api.get("/ai/threads", { params: { limit: 30 } });
      const rows: StoredThread[] = res.data?.data?.data ?? [];
      set({
        threads: rows.map((row) => ({
          id: row.id,
          title: row.title,
          updated_at: secondsOf(row.updated_at),
        })),
      });
    } catch (error) {
      set({ threads: [], threadsError: getError(error) });
    } finally {
      set({ isLoadingThreads: false });
    }
  },

  openThread: async (id) => {
    if (get().isOpeningThread) return false;
    set({ isOpeningThread: true, threadsError: null });
    try {
      const res = await api.get("/ai/thread", { params: { id } });
      const stored: StoredMessage[] = res.data?.data?.data?.messages ?? [];

      rememberThreadId(id);
      set({
        threadId: id,
        pendingAction: null,
        messages: stored
          .filter((message) => message.role === "user" || message.role === "assistant")
          .map((message) => ({
            id: message.id,
            role: message.role as ChatMessage["role"],
            content: message.content,
            timestamp: secondsOf(message.created_at) ?? Date.now(),
            ...(message.tool_calls?.length ? { toolCalls: message.tool_calls } : {}),
          })),
      });
      return true;
    } catch (error) {
      set({ threadsError: getError(error) });
      return false;
    } finally {
      set({ isOpeningThread: false });
    }
  },

  removeThread: async (id) => {
    const { threadId, clearMessages } = get();
    set({ threads: get().threads.filter((thread) => thread.id !== id) });

    try {
      await api.delete("/ai/thread", { params: { id } });
      threadId === id && clearMessages();
    } catch (error) {
      set({ threadsError: getError(error) });
      await get().loadThreads();
    }
  },

  restoreLastThread: async () => {
    const stored = readStoredThreadId();
    if (!stored || get().messages.length > 0) return false;

    const opened = await get().openThread(stored);
    if (!opened) rememberThreadId(null);
    return opened && get().messages.length > 0;
  },
  };
});
